import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { getLoginCookieAndSession } from "../utils";
import { getStripeConnectAccountStatus } from "../pages/Seller/seller-setup-api";
import type { StripeAccountStatusCode } from "../pages/Seller/seller-setup-types";

const PENDING_POLL_INTERVAL_MS = 60 * 1000;
const STRIPE_STATUS_CHANGED_EVENT = "stripe-account-status-changed";
const VERIFICATION_ERROR =
  "We couldn't confirm your seller verification. Please try again.";

export function notifyStripeAccountStatusChanged() {
  window.dispatchEvent(new Event(STRIPE_STATUS_CHANGED_EVENT));
}

interface SellerCheck {
  profileId: string | null;
  pathname: string;
  status: StripeAccountStatusCode | null;
  onboardingStep: number | null;
  loading: boolean;
  error: string | null;
}

/** Unknown, loading and failed checks never grant selling access or claim that
 * onboarding is incomplete. /sell presents the loading/error state in that case. */
export const useStripeAccountStatus = () => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const updateSearch = useRef(setSearchParams);
  updateSearch.current = setSearchParams;
  const latestSearch = useRef(searchParams.toString());
  latestSearch.current = searchParams.toString();
  const loginProfileId = getLoginCookieAndSession();
  const profileId = loginProfileId ? String(loginProfileId) : null;
  const [check, setCheck] = useState<SellerCheck>({
    profileId: null,
    pathname: "",
    status: null,
    onboardingStep: null,
    loading: true,
    error: null,
  });
  const [refreshToken, setRefreshToken] = useState(0);
  const [secondsUntilNextCheck, setSecondsUntilNextCheck] = useState<
    number | null
  >(null);
  const retry = useCallback(() => {
    setCheck((previous) => ({
      ...previous,
      loading: true,
      error: null,
      status: null,
    }));
    setRefreshToken((token) => token + 1);
  }, []);

  useEffect(() => {
    window.addEventListener(STRIPE_STATUS_CHANGED_EVENT, retry);
    return () => window.removeEventListener(STRIPE_STATUS_CHANGED_EVENT, retry);
  }, [retry]);

  useEffect(() => {
    const pathname = location.pathname;
    let cancelled = false;
    let poll: ReturnType<typeof setTimeout> | undefined;
    let countdown: ReturnType<typeof setInterval> | undefined;
    let started = false;
    setSecondsUntilNextCheck(null);
    setCheck({
      profileId,
      pathname,
      status: null,
      onboardingStep: null,
      loading: !!profileId,
      error: null,
    });
    if (!profileId) return;

    // Preserve the existing explicit return-from-Stripe behavior. Removing the
    // query flag does not start a second status request on this same route.
    const params = new URLSearchParams(latestSearch.current);
    const returnFromStripe = params.get("refresh") === "true";
    if (returnFromStripe) {
      params.delete("refresh");
      updateSearch.current(params, { replace: true });
    }

    const stopCountdown = () => {
      if (countdown) clearInterval(countdown);
      countdown = undefined;
      setSecondsUntilNextCheck(null);
    };
    const schedulePoll = () => {
      if (cancelled) return;
      poll = setTimeout(() => fetchStatus(), PENDING_POLL_INTERVAL_MS);
      setSecondsUntilNextCheck(PENDING_POLL_INTERVAL_MS / 1000);
      if (countdown) clearInterval(countdown);
      countdown = setInterval(
        () =>
          setSecondsUntilNextCheck((seconds) =>
            seconds === null || seconds <= 1 ? 0 : seconds - 1,
          ),
        1000,
      );
    };
    const fetchStatus = async (returned?: boolean) => {
      try {
        const result = await getStripeConnectAccountStatus(profileId, returned);
        if (cancelled) return;
        started = !!result?.stripeAccountId;
        setCheck({
          profileId,
          pathname,
          status: result?.status ?? "PENDING",
          onboardingStep: result?.onboardingStep ?? null,
          loading: false,
          error: null,
        });
        if (started && result?.status !== "ACTIVE") schedulePoll();
        else stopCountdown();
      } catch {
        if (cancelled) return;
        // Do not retain an old ACTIVE result after a failed recheck.
        setCheck({
          profileId,
          pathname,
          status: null,
          onboardingStep: null,
          loading: false,
          error: VERIFICATION_ERROR,
        });
        stopCountdown();
        if (started) schedulePoll();
      }
    };
    void fetchStatus(returnFromStripe || undefined);
    return () => {
      cancelled = true;
      if (poll) clearTimeout(poll);
      if (countdown) clearInterval(countdown);
    };
  }, [profileId, location.pathname, refreshToken]);

  // Account/route changes are unknown immediately, before effects run. Never
  // reuse another account's ACTIVE result or redirect using a previous route's check.
  const current =
    check.profileId === profileId && check.pathname === location.pathname;
  const loading = !!profileId && (!current || check.loading);
  const error = current ? check.error : null;
  const status = current && !loading && !error ? check.status : null;
  const isApproved = status === "ACTIVE";
  const hasCompletedOnboarding = current && check.onboardingStep === 5;
  const needsAction =
    !loading &&
    !error &&
    hasCompletedOnboarding &&
    (status === "ACTION_REQUIRED" || status === "RESTRICTED");
  const isVerificationPending =
    !loading && !error && hasCompletedOnboarding && !isApproved && !needsAction;
  const sellRoute =
    loading || error || (profileId && status === null) || isApproved
      ? "/sell"
      : hasCompletedOnboarding
        ? "/seller-setup"
        : "/presell";

  return {
    status,
    loading,
    error,
    retry,
    isApproved,
    isVerificationPending,
    needsAction,
    sellRoute,
    secondsUntilNextCheck,
  };
};
